import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, ChevronsLeftIcon, ChevronsRightIcon, HeartIcon, HomeIcon, SearchIcon, ShirtIcon, ShoppingCartIcon, TruckIcon } from 'lucide-react';
import { useSession } from '../contexts/SessionContext';
import { storeCategories, storePromos } from '../data/site';
type StoreLayoutProps = {
  category: string;
  productName: string;
  children: React.ReactNode;
};
const promoIcons = {
  shirt: ShirtIcon,
  truck: TruckIcon,
  heart: HeartIcon
};
export function StoreLayout({
  category,
  productName,
  children
}: StoreLayoutProps) {
  const {
    isLoggedIn,
    cartCount
  } = useSession();
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  return <>
      <div className="flex h-[120px] items-center justify-center gap-5 border-b-4 border-navy bg-[#0B6AA5] px-6" data-id="element-214" data-mp="1jbm84ifrh6jv">
        <ChevronsLeftIcon className="h-8 w-8 text-brand-sky" aria-hidden data-id="element-215" data-mp="3dnk8rw008u45" />
        <h1 className="font-condensed text-4xl font-semibold uppercase tracking-tight text-white md:text-[42px]" data-id="element-216" data-mp="3dlpu2zfbty8i">{category}</h1>
        <ChevronsRightIcon className="h-8 w-8 text-brand-sky" aria-hidden data-id="element-217" data-mp="25rfwrf7ww9x9" />
      </div>

      <div className="grid lg:grid-cols-[230px_1fr]" data-id="element-218" data-mp="2jikv8dzjmw60">
        <aside className="hidden border-r border-gray-200 px-5 py-4 lg:block" aria-label="Store navigation" data-id="element-219" data-mp="2ji2t50q79nq6">
          <form role="search" onSubmit={e => e.preventDefault()} className="flex items-center border-b border-navy/60" data-id="element-220" data-mp="2fhsmdlul2ta9">
            <label htmlFor="store-search" className="sr-only" data-id="element-221" data-mp="1rt9cj1vvy8g2">Search for products</label>
            <input id="store-search" placeholder="Search for products..." className="h-9 min-w-0 flex-1 text-sm outline-none placeholder:text-gray-400" data-id="element-222" data-mp="1muejl747f3i8" />
            <SearchIcon className="h-4 w-4" aria-hidden data-id="element-223" data-mp="1vryp4s93nlyn" />
          </form>
          <ul className="mt-5 space-y-3 text-[15px] text-navy" data-id="element-224" data-mp="3c701oc4m065q">
            <li className="flex items-center gap-2.5" data-id="element-225" data-mp="3c9cop3p856pn">
              <HomeIcon className="h-5 w-5 text-[#0B6AA5]" aria-hidden data-id="element-226" data-mp="3775820tg92gz" /> Store Home
            </li>
            <li className="flex items-center gap-2.5" data-id="element-227" data-mp="24tphxgsnzs9i">
              {isLoggedIn && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0C5D9E] text-xs text-white" data-id="element-228" data-mp="29tlnhkh0frr6">{cartCount}</span>}
              <ShoppingCartIcon className="h-5 w-5" aria-hidden data-id="element-229" data-mp="2dwqofilfzldp" /> My Cart
            </li>
          </ul>
          <button onClick={() => setCategoriesOpen(o => !o)} aria-expanded={categoriesOpen} className="mt-7 flex w-full items-center justify-between text-[17px] font-semibold text-navy" data-id="element-230" data-mp="2it8rme14jbnx">
            Store Categories
            {categoriesOpen ? <ChevronUpIcon className="h-4 w-4" aria-hidden data-id="element-231" data-mp="1v3whnbxz2zkk" /> : <ChevronDownIcon className="h-4 w-4" aria-hidden data-id="element-232" data-mp="1v34k3vkterrn" />}
          </button>
          {categoriesOpen && <ul className="mt-2 space-y-2.5 pl-4 text-[15px] text-[#1F3BB3]" data-id="element-233" data-mp="12gjv31rtarr9">
              {storeCategories.map(c => <li key={c.label} className={`flex items-center justify-between ${c.label === category ? 'font-semibold' : ''}`} data-id="element-234" data-mp="17elcli9laj0m">
                  {c.label}
                  {c.hasMenu && <ChevronDownIcon className="h-4 w-4 text-navy" aria-hidden data-id="element-235" data-mp="ux0trfepabtv" />}
                </li>)}
            </ul>}
        </aside>

        <div className="min-w-0" data-id="element-236" data-mp="pvuzhd5e6gi8">
          <nav aria-label="Breadcrumb" className="px-7 py-4" data-id="element-237" data-mp="lw2z3nq5jgny">
            <ol className="flex flex-wrap items-center gap-2 text-[15px]" data-id="element-238" data-mp="lvt8bxxa95h7">
              <li data-id="element-239" data-mp="gy1euzgzpbaw"><Link to="/" className="text-[#1F3BB3] underline" data-id="element-240" data-mp="gxrqfgblfc7b">Home</Link></li>
              <ChevronsRightIcon className="h-4 w-4 text-[#1F3BB3]" aria-hidden data-id="element-241" data-mp="c5zwcvt8aocf" />
              <li className="text-[#1F3BB3] underline" data-id="element-242" data-mp="76x1u5avk0ip">Store Home</li>
              <ChevronsRightIcon className="h-4 w-4 text-[#1F3BB3]" aria-hidden data-id="element-243" data-mp="753wqufwtoct" />
              <li className="text-[#1F3BB3] underline" data-id="element-244" data-mp="29og5uqipecp">{category}</li>
              <ChevronsRightIcon className="h-4 w-4 text-[#1F3BB3]" aria-hidden data-id="element-245" data-mp="3uausgzq70f6x" />
              <li aria-current="page" className="text-gray-500" data-id="element-246" data-mp="3pc9s90axxvih">{productName}</li>
            </ol>
          </nav>

          <ul className="hidden border-y border-gray-200 md:grid md:grid-cols-3" data-id="element-247" data-mp="1t9evhljtmakh">
            {storePromos.map(p => {
            const Icon = promoIcons[p.icon];
            return <li key={p.title} className="flex items-center gap-3 border-r border-gray-200 px-5 py-4 last:border-r-0" data-id="element-248" data-mp="1jkn3xeio9sa3">
                  <Icon className="h-7 w-7 shrink-0 text-brand-red" aria-hidden data-id="element-249" data-mp="1ohn8tm3scozm" />
                  <div className="min-w-0" data-id="element-250" data-mp="19kminn18t239">
                    <p className="font-bold uppercase" data-id="element-251" data-mp="19ns9b6pisc13">{p.title}</p>
                    <p className="truncate text-sm" data-id="element-252" data-mp="1eljy3ruvbi12">{p.body}</p>
                  </div>
                </li>;
          })}
          </ul>

          {children}
        </div>
      </div>
    </>;
}